import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Box, 
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    CircularProgress,
    Chip,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    MenuItem,
    Alert,
    Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getFirestore, collection, getDocs, query, orderBy, addDoc, updateDoc, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { ciudades } from '../../../utils/default';

const firestore = getFirestore();

const estados = [
    { value: 'activa', label: 'Activa', color: '#4caf50' },
    { value: 'en_proceso', label: 'En Proceso', color: '#2196f3' },
    { value: 'detenida', label: 'Detenida', color: '#ff9800' },
    { value: 'finalizada', label: 'Finalizada', color: '#9e9e9e' }
];

function ListObras() {
    const [obras, setObras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [estadoFilter, setEstadoFilter] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingObra, setEditingObra] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        localidad: '',
        tipo: '',
        estado: 'activa'
    });
    const [isOtraLocalidad, setIsOtraLocalidad] = useState(false);
    const [localidadCustom, setLocalidadCustom] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, obra: null });

    useEffect(() => {
        fetchObras();
    }, []);

    const fetchObras = async () => {
        try {
            setLoading(true);
            const q = query(
                collection(firestore, 'obras_huawei'),
                orderBy('id', 'asc')
            );
            const querySnapshot = await getDocs(q);
            const obrasData = [];
            
            querySnapshot.forEach((docSnap) => {
                obrasData.push({ ...docSnap.data(), docId: docSnap.id });
            });
            
            setObras(obrasData);
        } catch (error) {
            console.error('Error al cargar obras:', error);
            showSnackbar('Error al cargar las obras', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (obra = null) => {
        if (obra) {
            setEditingObra(obra);
            const localidadIsCustom = obra.localidad && !ciudades.includes(obra.localidad);
            setIsOtraLocalidad(localidadIsCustom);
            setFormData({
                nombre: obra.nombre || '',
                localidad: localidadIsCustom ? 'OTRO' : (obra.localidad || ''),
                tipo: obra.tipo || '',
                estado: obra.estado || 'activa'
            });
            setLocalidadCustom(localidadIsCustom ? obra.localidad : '');
        } else {
            setEditingObra(null);
            setIsOtraLocalidad(false);
            setLocalidadCustom('');
            setFormData({
                nombre: '',
                localidad: '',
                tipo: '',
                estado: 'activa'
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingObra(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'localidad') {
            if (value === 'OTRO') {
                setIsOtraLocalidad(true);
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            } else {
                setIsOtraLocalidad(false);
                setLocalidadCustom('');
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async () => {
        try {
            // Validaciones
            const finalLocalidad = isOtraLocalidad ? localidadCustom : formData.localidad;
            
            if (!formData.nombre || !finalLocalidad) {
                showSnackbar('Nombre y localidad son obligatorios', 'warning');
                return;
            }

            const dataToSave = {
                nombre: formData.nombre,
                localidad: finalLocalidad,
                tipo: formData.tipo,
                estado: formData.estado
            };

            if (editingObra) {
                // Actualizar obra existente
                const docRef = doc(firestore, 'obras_huawei', editingObra.docId);
                await updateDoc(docRef, dataToSave);
                showSnackbar('Obra actualizada exitosamente', 'success');
            } else {
                // Crear nueva obra
                // Obtener el ID más alto para asignar el siguiente
                let maxId = 0;
                if (obras.length > 0) {
                    maxId = Math.max(...obras.map(p => parseInt(p.id) || 0));
                }
                
                const newId = maxId + 1;
                dataToSave.id = newId;
                
                await setDoc(doc(firestore, 'obras_huawei', newId.toString()), dataToSave);
                showSnackbar('Obra creada exitosamente', 'success');
            }

            handleCloseDialog();
            fetchObras();
        } catch (error) {
            console.error('Error al guardar obra:', error);
            showSnackbar('Error al guardar la obra', 'error');
        }
    };

    const handleDelete = async (obra) => {
        setDeleteConfirm({ open: true, obra });
    };

    const confirmDelete = async () => {
        try {
            const docRef = doc(firestore, 'obras_huawei', deleteConfirm.obra.docId);
            await deleteDoc(docRef);
            showSnackbar('Obra eliminada exitosamente', 'success');
            setDeleteConfirm({ open: false, obra: null });
            fetchObras();
        } catch (error) {
            console.error('Error al eliminar obra:', error);
            showSnackbar('Error al eliminar la obra', 'error');
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const getEstadoInfo = (estado) => {
        return estados.find(e => e.value === estado) || estados[0];
    };

    const handleEstadoFilterClick = (estadoValue) => {
        if (estadoFilter === estadoValue) {
            setEstadoFilter(null);
        } else {
            setEstadoFilter(estadoValue);
        }
    };

    const filteredObras = obras.filter(obra => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = (
            obra.nombre?.toLowerCase().includes(searchLower) ||
            obra.localidad?.toLowerCase().includes(searchLower) ||
            obra.tipo?.toLowerCase().includes(searchLower)
        );
        
        const matchesEstado = estadoFilter === null || obra.estado === estadoFilter;
        
        return matchesSearch && matchesEstado;
    });

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        Obras - Huawei
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Nueva Obra
                    </Button>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        label="Buscar por nombre, localidad o tipo"
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Ej: Obra Central, Asunción..."
                    />
                </Box>

                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                        label={`Total: ${obras.length} obras`} 
                        color="primary" 
                        variant={estadoFilter === null ? "filled" : "outlined"}
                        onClick={() => setEstadoFilter(null)}
                        sx={{ cursor: 'pointer' }}
                    />
                    {estados.map(estado => {
                        const count = obras.filter(p => p.estado === estado.value).length;
                        const isActive = estadoFilter === estado.value;
                        return (
                            <Chip 
                                key={estado.value}
                                label={`${estado.label}: ${count}`}
                                onClick={() => handleEstadoFilterClick(estado.value)}
                                sx={{ 
                                    backgroundColor: isActive ? estado.color : `${estado.color}80`,
                                    color: '#fff',
                                    fontWeight: isActive ? 'bold' : 'normal',
                                    cursor: 'pointer',
                                    border: isActive ? '2px solid #000' : 'none',
                                    '&:hover': {
                                        backgroundColor: estado.color,
                                        fontWeight: 'bold'
                                    }
                                }}
                            />
                        );
                    })}
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Localidad</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredObras.map((obra) => {
                                const estadoInfo = getEstadoInfo(obra.estado);
                                return (
                                    <TableRow 
                                        key={obra.docId}
                                        sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                                    >
                                        <TableCell>{obra.id}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{obra.nombre}</TableCell>
                                        <TableCell>{obra.localidad}</TableCell>
                                        <TableCell>{obra.tipo}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={estadoInfo.label}
                                                sx={{ 
                                                    backgroundColor: estadoInfo.color,
                                                    color: '#fff',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            <IconButton
                                                color="primary"
                                                size="small"
                                                onClick={() => handleOpenDialog(obra)}
                                                title="Editar"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                color="error"
                                                size="small"
                                                onClick={() => handleDelete(obra)}
                                                title="Eliminar"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {filteredObras.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchTerm ? `No se encontraron resultados para "${searchTerm}"` : 'No hay obras registradas. Crea una nueva obra para comenzar.'}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Dialog para Crear/Editar */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingObra ? 'Editar Obra' : 'Nueva Obra'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nombre de la Obra *"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Expansión Red Central"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Localidad *"
                                    name="localidad"
                                    value={formData.localidad}
                                    onChange={handleInputChange}
                                >
                                    {ciudades.map((ciudad) => (
                                        <MenuItem key={ciudad} value={ciudad}>
                                            {ciudad}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            {isOtraLocalidad && (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Especificar Localidad *"
                                        value={localidadCustom}
                                        onChange={(e) => setLocalidadCustom(e.target.value)}
                                        placeholder="Ej: Ciudad del Este"
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Tipo"
                                    name="tipo"
                                    value={formData.tipo}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Instalación, Mantenimiento"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Estado *"
                                    name="estado"
                                    value={formData.estado}
                                    onChange={handleInputChange}
                                >
                                    {estados.map((estado) => (
                                        <MenuItem key={estado.value} value={estado.value}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box 
                                                    sx={{ 
                                                        width: 16, 
                                                        height: 16, 
                                                        borderRadius: '50%', 
                                                        backgroundColor: estado.color 
                                                    }} 
                                                />
                                                {estado.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained"
                        color="primary"
                    >
                        {editingObra ? 'Actualizar' : 'Crear'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de Confirmación de Eliminación */}
            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, obra: null })}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de que deseas eliminar la obra "{deleteConfirm.obra?.nombre}"?
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Esta acción no se puede deshacer.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, obra: null })}>
                        Cancelar
                    </Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default ListObras;
